"use client";

import type { GdprConsent } from "agir-types";
import type { ReactNode } from "react";
import type { CookieValue, Service } from "vanilla-cookieconsent";

import "@orestbida/iframemanager/dist/iframemanager.css";
import "@orestbida/iframemanager/dist/iframemanager.js";
import "vanilla-cookieconsent/dist/cookieconsent.css";

import "@/src/infrastructure/cookieconsent/iframemanager.css";

import { usePathname } from "next/navigation";
import { useEffect, useMemo, useRef } from "react";
import { getCookie, getUserPreferences, run } from "vanilla-cookieconsent";

import type { CookieService } from "@/src/infrastructure/cookieconsent/CookieService";
import type { IframeManagerChange } from "@/src/infrastructure/cookieconsent/IframeManager";

import { airtableCookie } from "@/src/infrastructure/airtable/airtable";
import { saveGdprConsent } from "@/src/infrastructure/api/gdprConsentsRepository";
import { auth0Cookie } from "@/src/infrastructure/auth0/auth0Cookie";
import {
  acceptServiceWhenReady,
  setCookieConsentInitResult,
} from "@/src/infrastructure/cookieconsent/consent";
import { CookieCategory } from "@/src/infrastructure/cookieconsent/CookieCategory";
import {
  iframeLanguages,
  language,
  showPreferencesLabel,
} from "@/src/infrastructure/cookieconsent/cookieConsentI18n";
import { cookieLabel } from "@/src/infrastructure/cookieconsent/cookieLabel";
import { datadogCookie, logger } from "@/src/infrastructure/datadog/datadog";
import { fastCommentsCookie } from "@/src/infrastructure/fastcomments/fastComments";
import { googleMapsCookie } from "@/src/infrastructure/google-maps/googleMaps";
import { mondayCookie } from "@/src/infrastructure/monday/monday";
import { strapiCookie } from "@/src/infrastructure/strapi/strapiCookie";
import { stripeCookie } from "@/src/infrastructure/stripe/stripeConfig";
import { surveyCookie } from "@/src/infrastructure/surveyjs/surveyCookie";
import { youTubeCookie } from "@/src/infrastructure/youtube/YouTubeVideo";

const services = [
  datadogCookie,
  airtableCookie,
  fastCommentsCookie,
  googleMapsCookie,
  mondayCookie,
  youTubeCookie,
  auth0Cookie,
  strapiCookie,
  stripeCookie,
  surveyCookie,
];

export function CookieConsent(): ReactNode {
  // iframeManager must be reset/run on each page change
  // https://github.com/orestbida/iframemanager/issues/48
  const pathname = usePathname();

  const iframeManager = useMemo(
    () => (typeof iframemanager === "undefined" ? null : iframemanager()),
    [],
  );

  // Unlike iframeManager, the consent library itself only needs run() once.
  const hasRunOnce = useRef(false);

  useEffect(() => {
    if (!iframeManager) {
      return;
    }

    iframeManager.run({
      currLang: language.default,
      onChange: iframeManagerOnChange,
      services: Object.fromEntries(
        services
          .filter((s) => s.iframe)
          .map((s) => [s.name, { ...s.iframe, languages: iframeLanguages(s) }]),
      ),
    });

    function iframeService(service: CookieService): Service {
      return {
        onAccept: (): void => {
          iframeManager?.acceptService(service.name);
        },
        onReject: (): void => {
          iframeManager?.rejectService(service.name);
        },
      };
    }

    if (!hasRunOnce.current) {
      hasRunOnce.current = true;
      const categories = groupByCategory(services, (s) => ({
        ...s.service,
        ...(s.iframe ? iframeService(s) : undefined),
        label: cookieLabel(s),
      }));
      const initResult = run({
        categories: {
          ...categories,
          [CookieCategory.NECESSARY]: {
            ...categories[CookieCategory.NECESSARY],
            enabled: true,
            readOnly: true,
          },
        },
        language,
        onChange: log,
        onFirstConsent: log,
      });
      setCookieConsentInitResult(initResult);
    }

    return (): void => {
      iframeManager.reset();
    };
  }, [iframeManager, pathname]);

  return (
    <button
      className="cursor-pointer"
      data-cc="show-preferencesModal"
      type="button"
    >
      {showPreferencesLabel}
    </button>
  );
}

function groupByCategory(
  cookieServices: CookieService[],
  toService: (s: CookieService) => Service,
): Partial<Record<CookieCategory, { services: Record<string, Service> }>> {
  const result: Partial<
    Record<CookieCategory, { services: Record<string, Service> }>
  > = {};
  for (const s of cookieServices) {
    result[s.category] ??= { services: {} };
    result[s.category]!.services[s.name] = toService(s);
  }
  return result;
}

// https://cookieconsent.orestbida.com/advanced/iframemanager-setup.html
function iframeManagerOnChange(change: IframeManagerChange): void {
  if (change.eventSource.type !== "click") {
    return;
  }
  // This click can happen before run() (above) finishes registering the
  // service; acceptServiceWhenReady queues it instead of dropping it.
  acceptServiceWhenReady(() => {
    const prefs = getUserPreferences() as {
      acceptedServices?: Partial<Record<CookieCategory, string[]>>;
    };
    const currentFeatureServices =
      prefs.acceptedServices?.[CookieCategory.FEATURES] ?? [];
    return [...currentFeatureServices, ...change.changedServices];
  }, CookieCategory.FEATURES);
}

function log(): void {
  const preferences = getUserPreferences();
  const consent: GdprConsent = {
    categories: preferences.acceptedCategories,
    date: new Date().toISOString(),
    id: (getCookie() as CookieValue | undefined)?.consentId ?? "",
    services: Object.entries(preferences.acceptedServices).flatMap(
      ([key, values]) => values.map((value) => `${key}:${value}`),
    ),
    type: preferences.acceptType,
  };
  void saveGdprConsent(consent).catch((error: unknown) => {
    logger.warn(
      "Failed to save GDPR consent",
      {},
      error instanceof Error ? error : undefined,
    );
  });
}
